import { useState, useEffect, useCallback } from "react";
import QMSLayout from "../components/QMSLayout";
import { useTranslation } from "react-i18next";
import {
  MessageCircle, Mail, Video, Phone, ExternalLink,
  Settings, ChevronRight, Lock, Zap, Link2, Unlink,
  Inbox, Send, FileText, RefreshCw, ArrowLeft, Search,
  Paperclip, Star, Clock, ChevronDown, X, Plus, Loader2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CommunicationsPage = () => {
  const { t } = useTranslation('platform');
  const [graphStatus, setGraphStatus] = useState(null);
  const [activeView, setActiveView] = useState("hub");
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [teamsChats, setTeamsChats] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailFolder, setEmailFolder] = useState("inbox");
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeData, setComposeData] = useState({ to: "", cc: "", subject: "", body: "" });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [newChatMessage, setNewChatMessage] = useState("");

  const fetchGraphStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/graph/status`, { credentials: "include" });
      if (res.ok) setGraphStatus(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    fetchGraphStatus();
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") === "true") {
      fetchGraphStatus();
      window.history.replaceState({}, "", "/communications");
    }
  }, [fetchGraphStatus]);

  const connectMicrosoft = async () => {
    try {
      const res = await fetch(`${API}/api/graph/connect`, { credentials: "include" });
      const data = await res.json();
      if (data.auth_url) window.location.href = data.auth_url;
    } catch (e) { console.error(e); }
  };

  const disconnectMicrosoft = async () => {
    await fetch(`${API}/api/graph/disconnect`, { method: "DELETE", credentials: "include" });
    setGraphStatus({ connected: false });
    setActiveView("hub");
  };

  const fetchEmails = async (folder = "inbox") => {
    setLoading(true);
    setEmailFolder(folder);
    try {
      const res = await fetch(`${API}/api/graph/outlook/messages?folder=${folder}`, { credentials: "include" });
      if (res.ok) { const data = await res.json(); setEmails(data.messages || []); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchEmailDetail = async (id) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/graph/outlook/messages/${id}`, { credentials: "include" });
      if (res.ok) setSelectedEmail(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const sendEmail = async () => {
    setSendingEmail(true);
    try {
      const res = await fetch(`${API}/api/graph/outlook/send`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: composeData.to.split(",").map(s => s.trim()),
          cc: composeData.cc ? composeData.cc.split(",").map(s => s.trim()) : [],
          subject: composeData.subject,
          body: composeData.body,
        }),
      });
      if (res.ok) {
        setComposeOpen(false);
        setComposeData({ to: "", cc: "", subject: "", body: "" });
        fetchEmails(emailFolder);
      }
    } catch (e) { console.error(e); }
    setSendingEmail(false);
  };

  const fetchTeamsChats = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/graph/teams/chats`, { credentials: "include" });
      if (res.ok) { const data = await res.json(); setTeamsChats(data.chats || []); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchChatMessages = async (chatId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/graph/teams/chats/${chatId}/messages`, { credentials: "include" });
      if (res.ok) { const data = await res.json(); setChatMessages(data.messages || []); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const sendChatMessage = async () => {
    if (!newChatMessage.trim() || !selectedChat) return;
    try {
      await fetch(`${API}/api/graph/teams/chats/${selectedChat.id}/messages`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: newChatMessage }),
      });
      setNewChatMessage("");
      fetchChatMessages(selectedChat.id);
    } catch (e) { console.error(e); }
  };

  const connected = graphStatus?.connected;

  const INTEGRATIONS = [
    {
      id: "internal", name: "Internal Messenger",
      desc: t('platform:communications.inAppDesc'),
      icon: MessageCircle, status: "active", color: "#4ADE80", link: "/messages",
    },
    {
      id: "outlook", name: "Microsoft Outlook",
      desc: connected ? t('platform:communications.outlookConnected', { email: graphStatus.microsoft_email }) : t('platform:communications.outlookDesc'),
      icon: Mail, status: connected ? "active" : "connect", color: "#38BDF8",
      action: connected ? () => { setActiveView("outlook"); fetchEmails(); } : connectMicrosoft,
    },
    {
      id: "teams", name: "Microsoft Teams",
      desc: connected ? t('platform:communications.teamsConnected') : t('platform:communications.teamsDesc'),
      icon: Video, status: connected ? "active" : "connect", color: "#A78BFA",
      action: connected ? () => { setActiveView("teams"); fetchTeamsChats(); } : connectMicrosoft,
    },
    {
      id: "whatsapp", name: "WhatsApp Business",
      desc: t('platform:communications.smsDesc'),
      icon: Phone, status: "configured", color: "#4ADE80",
    },
    {
      id: "email", name: "Email Notifications",
      desc: "Automated alerts for audit schedules, CAPA deadlines, and approvals",
      icon: Mail, status: "configured", color: "#E8A838", link: "/notification-settings",
    },
  ];

  const statusConfig = {
    active: { label: "Active", color: "#4ADE80", bg: "#4ADE8020" },
    configured: { label: "Configured", color: "#E8A838", bg: "#E8A83820" },
    connect: { label: "Connect", color: "#38BDF8", bg: "#38BDF820" },
    coming_soon: { label: "Coming Soon", color: "#8B95A5", bg: "#8B95A520" },
  };

  // ============== OUTLOOK VIEW ==============
  if (activeView === "outlook") {
    return (
      <QMSLayout title={t('platform:communications.title')}>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <button onClick={() => { setActiveView("hub"); setSelectedEmail(null); }}
              className="p-2 rounded hover:bg-[#1F222D] text-[#8B95A5]" data-testid="outlook-back">
              <ArrowLeft size={18} />
            </button>
            <div>
              <h2 className="font-['Syne'] text-xl font-bold text-white" data-testid="outlook-title">Outlook Mail</h2>
              <p className="text-[#8B95A5] text-xs">{graphStatus?.microsoft_email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setComposeOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#C9A84C] text-[#0B0E14] rounded text-xs font-medium hover:bg-[#D4B85C]"
              data-testid="compose-email-btn">
              <Plus size={14} /> Compose
            </button>
            <button onClick={() => fetchEmails(emailFolder)}
              className="p-2 rounded hover:bg-[#1F222D] text-[#8B95A5]" data-testid="refresh-emails">
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {/* Folder tabs */}
        <div className="flex gap-1 mb-4">
          {[["inbox", t('platform:communications.inbox'), Inbox], ["sent", t('platform:communications.sent'), Send], ["drafts", t('platform:communications.drafts'), FileText]].map(([key, label, Icon]) => (
            <button key={key} onClick={() => { fetchEmails(key); setSelectedEmail(null); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                emailFolder === key ? "bg-[#C9A84C20] text-[#C9A84C]" : "text-[#8B95A5] hover:bg-[#1F222D]"
              }`} data-testid={`folder-${key}`}>
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>

        {/* Compose Modal */}
        {composeOpen && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-[#151820] border border-[rgba(255,255,255,0.08)] rounded-lg w-full max-w-2xl" data-testid="compose-modal">
              <div className="flex items-center justify-between p-4 border-b border-[rgba(255,255,255,0.06)]">
                <h3 className="text-white font-semibold text-sm">New Email</h3>
                <button onClick={() => setComposeOpen(false)} className="text-[#8B95A5] hover:text-white"><X size={16} /></button>
              </div>
              <div className="p-4 space-y-3">
                <input value={composeData.to} onChange={e => setComposeData({...composeData, to: e.target.value})}
                  placeholder="To (comma-separated)" className="w-full bg-[#1A1D27] border border-[rgba(255,255,255,0.08)] rounded px-3 py-2 text-white text-sm"
                  data-testid="compose-to" />
                <input value={composeData.cc} onChange={e => setComposeData({...composeData, cc: e.target.value})}
                  placeholder="CC (optional)" className="w-full bg-[#1A1D27] border border-[rgba(255,255,255,0.08)] rounded px-3 py-2 text-white text-sm"
                  data-testid="compose-cc" />
                <input value={composeData.subject} onChange={e => setComposeData({...composeData, subject: e.target.value})}
                  placeholder="Subject" className="w-full bg-[#1A1D27] border border-[rgba(255,255,255,0.08)] rounded px-3 py-2 text-white text-sm"
                  data-testid="compose-subject" />
                <textarea value={composeData.body} onChange={e => setComposeData({...composeData, body: e.target.value})}
                  placeholder={t('platform:communications.messageBody')} rows={8}
                  className="w-full bg-[#1A1D27] border border-[rgba(255,255,255,0.08)] rounded px-3 py-2 text-white text-sm resize-none"
                  data-testid="compose-body" />
              </div>
              <div className="flex justify-end p-4 border-t border-[rgba(255,255,255,0.06)]">
                <button onClick={sendEmail} disabled={sendingEmail || !composeData.to || !composeData.subject}
                  className="flex items-center gap-1.5 px-4 py-2 bg-[#C9A84C] text-[#0B0E14] rounded text-xs font-medium hover:bg-[#D4B85C] disabled:opacity-50"
                  data-testid="send-email-btn">
                  {sendingEmail ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  {sendingEmail ? t('platform:communications.sending') : t('platform:communications.send')}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-4" style={{ height: "calc(100vh - 240px)" }}>
          {/* Email list */}
          <div className="w-2/5 overflow-y-auto space-y-1 pr-2">
            {loading && emails.length === 0 ? (
              <div className="flex items-center justify-center py-12 text-[#8B95A5] text-sm">
                <Loader2 size={18} className="animate-spin mr-2" /> Loading emails...
              </div>
            ) : emails.length === 0 ? (
              <div className="text-center py-12 text-[#8B95A5] text-sm">No emails in this folder</div>
            ) : emails.map(email => (
              <div key={email.id} onClick={() => fetchEmailDetail(email.id)}
                className={`p-3 rounded cursor-pointer transition-colors border ${
                  selectedEmail?.id === email.id
                    ? "bg-[#C9A84C10] border-[#C9A84C30]"
                    : "border-transparent hover:bg-[#1A1D27]"
                }`} data-testid={`email-item-${email.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-medium truncate max-w-[200px] ${email.is_read ? "text-[#8B95A5]" : "text-white"}`}>
                    {email.from_name || email.from_email}
                  </span>
                  <span className="text-[#555] text-[10px] flex-shrink-0">
                    {email.received_at ? new Date(email.received_at).toLocaleDateString() : ""}
                  </span>
                </div>
                <p className={`text-xs truncate ${email.is_read ? "text-[#666]" : "text-[#CCC] font-medium"}`}>
                  {email.subject}
                </p>
                <p className="text-[#555] text-[10px] truncate mt-0.5">{email.preview}</p>
                <div className="flex items-center gap-2 mt-1">
                  {email.has_attachments && <Paperclip size={10} className="text-[#8B95A5]" />}
                  {email.importance === "high" && <Star size={10} className="text-[#E8A838]" />}
                  {!email.is_read && <div className="w-1.5 h-1.5 rounded-full bg-[#38BDF8]" />}
                </div>
              </div>
            ))}
          </div>

          {/* Email detail */}
          <div className="flex-1 overflow-y-auto">
            {selectedEmail ? (
              <div className="qms-card" data-testid="email-detail">
                <div className="qms-card-body">
                  <h3 className="text-white font-semibold text-base mb-3">{selectedEmail.subject}</h3>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full bg-[#38BDF820] flex items-center justify-center text-[#38BDF8] text-xs font-bold">
                      {(selectedEmail.from_name || "?")[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="text-white text-xs font-medium">{selectedEmail.from_name}</p>
                      <p className="text-[#8B95A5] text-[10px]">{selectedEmail.from_email}</p>
                    </div>
                    <span className="ml-auto text-[#555] text-[10px]">
                      {selectedEmail.received_at ? new Date(selectedEmail.received_at).toLocaleString() : ""}
                    </span>
                  </div>
                  {selectedEmail.to?.length > 0 && (
                    <p className="text-[#8B95A5] text-[10px] mb-3">To: {selectedEmail.to.join(", ")}</p>
                  )}
                  <div className="border-t border-[rgba(255,255,255,0.06)] pt-3 mt-3">
                    <div className="text-[#CCC] text-sm leading-relaxed prose prose-invert prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: selectedEmail.body_html || "" }} />
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-[#8B95A5] text-sm">
                <Mail size={24} className="mr-2 opacity-50" /> Select an email to read
              </div>
            )}
          </div>
        </div>
      </QMSLayout>
    );
  }

  // ============== TEAMS VIEW ==============
  if (activeView === "teams") {
    return (
      <QMSLayout title={t('platform:communications.title')}>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <button onClick={() => { setActiveView("hub"); setSelectedChat(null); setChatMessages([]); }}
              className="p-2 rounded hover:bg-[#1F222D] text-[#8B95A5]" data-testid="teams-back">
              <ArrowLeft size={18} />
            </button>
            <div>
              <h2 className="font-['Syne'] text-xl font-bold text-white" data-testid="teams-title">Teams Chat</h2>
              <p className="text-[#8B95A5] text-xs">{graphStatus?.microsoft_email}</p>
            </div>
          </div>
          <button onClick={fetchTeamsChats}
            className="p-2 rounded hover:bg-[#1F222D] text-[#8B95A5]" data-testid="refresh-chats">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
        </div>

        <div className="flex gap-4" style={{ height: "calc(100vh - 200px)" }}>
          {/* Chat list */}
          <div className="w-1/3 overflow-y-auto space-y-1 pr-2">
            {loading && teamsChats.length === 0 ? (
              <div className="flex items-center justify-center py-12 text-[#8B95A5] text-sm">
                <Loader2 size={18} className="animate-spin mr-2" /> Loading chats...
              </div>
            ) : teamsChats.length === 0 ? (
              <div className="text-center py-12 text-[#8B95A5] text-sm">No Teams chats found</div>
            ) : teamsChats.map(chat => (
              <div key={chat.id} onClick={() => { setSelectedChat(chat); fetchChatMessages(chat.id); }}
                className={`p-3 rounded cursor-pointer transition-colors border ${
                  selectedChat?.id === chat.id
                    ? "bg-[#A78BFA10] border-[#A78BFA30]"
                    : "border-transparent hover:bg-[#1A1D27]"
                }`} data-testid={`chat-item-${chat.id}`}>
                <p className="text-white text-xs font-medium truncate">{chat.topic || chat.chat_type || "Chat"}</p>
                {chat.last_sender && (
                  <p className="text-[#8B95A5] text-[10px] mt-0.5">{chat.last_sender}</p>
                )}
                <p className="text-[#555] text-[10px] truncate mt-0.5"
                  dangerouslySetInnerHTML={{ __html: chat.last_message || t('platform:communications.noMessages') }} />
              </div>
            ))}
          </div>

          {/* Chat messages */}
          <div className="flex-1 flex flex-col">
            {selectedChat ? (
              <>
                <div className="flex-1 overflow-y-auto space-y-2 mb-3 p-3 bg-[#0D1017] rounded-lg" data-testid="chat-messages">
                  {chatMessages.length === 0 ? (
                    <div className="text-center py-12 text-[#8B95A5] text-sm">{t('platform:communications.noMessages')}</div>
                  ) : chatMessages.map(msg => (
                    <div key={msg.id} className="p-2 rounded bg-[#151820]">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[#A78BFA] text-[10px] font-medium">{msg.sender}</span>
                        <span className="text-[#555] text-[9px]">
                          {msg.created_at ? new Date(msg.created_at).toLocaleString() : ""}
                        </span>
                      </div>
                      <div className="text-[#CCC] text-xs" dangerouslySetInnerHTML={{ __html: msg.content }} />
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input value={newChatMessage} onChange={e => setNewChatMessage(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && sendChatMessage()}
                    placeholder={t('platform:communications.typeMessage')}
                    className="flex-1 bg-[#1A1D27] border border-[rgba(255,255,255,0.08)] rounded px-3 py-2 text-white text-sm"
                    data-testid="chat-input" />
                  <button onClick={sendChatMessage}
                    className="px-3 py-2 bg-[#A78BFA] text-white rounded text-xs font-medium hover:bg-[#B89CFC]"
                    data-testid="send-chat-btn">
                    <Send size={14} />
                  </button>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-[#8B95A5] text-sm">
                <Video size={24} className="mr-2 opacity-50" /> Select a chat to view messages
              </div>
            )}
          </div>
        </div>
      </QMSLayout>
    );
  }

  // ============== HUB VIEW ==============
  return (
    <QMSLayout title={t('platform:communications.title')}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-['Syne'] text-2xl font-bold text-white" data-testid="comms-title">Communications Hub</h2>
          <p className="text-[#8B95A5] text-sm mt-1">Integrated messaging, notifications, and external communication channels</p>
        </div>
        {connected && (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#4ADE8015] rounded text-[#4ADE80] text-xs">
              <Link2 size={12} /> Microsoft Connected
            </div>
            <button onClick={disconnectMicrosoft}
              className="flex items-center gap-1 px-2 py-1.5 rounded text-[#8B95A5] hover:text-red-400 hover:bg-red-400/10 text-xs"
              data-testid="disconnect-btn">
              <Unlink size={12} /> Disconnect
            </button>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {INTEGRATIONS.map((integ) => {
          const sc = statusConfig[integ.status];
          return (
            <div key={integ.id}
              onClick={() => {
                if (integ.link) window.location.href = integ.link;
                else if (integ.action) integ.action();
              }}
              className="qms-card cursor-pointer hover:border-[rgba(255,255,255,0.15)] transition-all"
              data-testid={`integration-${integ.id}`}>
              <div className="qms-card-body">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-[3px] flex items-center justify-center" style={{ backgroundColor: `${integ.color}15` }}>
                    <integ.icon size={20} style={{ color: integ.color }} strokeWidth={1.5} />
                  </div>
                  <span className="px-2.5 py-1 rounded text-[10px] font-medium" style={{ color: sc.color, backgroundColor: sc.bg }}>
                    {sc.label}
                  </span>
                </div>
                <h3 className="text-white font-semibold text-sm mb-1">{integ.name}</h3>
                <p className="text-[#8B95A5] text-xs leading-relaxed">{integ.desc}</p>
                {integ.status === "active" && (
                  <div className="mt-3 flex items-center gap-1 text-[#4ADE80] text-xs">
                    <Zap size={12} /> Connected <ChevronRight size={12} />
                  </div>
                )}
                {integ.status === "connect" && (
                  <div className="mt-3 flex items-center gap-1 text-[#38BDF8] text-xs">
                    <Link2 size={12} /> Click to connect <ChevronRight size={12} />
                  </div>
                )}
                {integ.status === "configured" && (
                  <div className="mt-3 flex items-center gap-1 text-[#E8A838] text-xs">
                    <Settings size={12} /> Configured <ChevronRight size={12} />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </QMSLayout>
  );
};

export default CommunicationsPage;
